import { useMemo } from 'react';
import type {
  WorkbookResult,
  WorkbookRun,
  WorkbookFilter,
} from '@erp/shared-types';
import { sampleResult, sheetFingerprints, shownResult } from '@erp/workbooks';
import type { WorkbookEditor } from './useWorkbookEditor';

/** What one Worksheet shows: which Run's Result, and whether it is Current. */
export type SheetRunResult = {
  /** The newest Run of any status, for queued/running/failed notices. */
  latestRun: WorkbookRun | undefined;
  /** The completed Run the Result comes from: the newest Current one, else the newest with this Worksheet, else the newest. */
  run: WorkbookRun | undefined;
  result: WorkbookResult | undefined;
  /** A Current Result can be exported and built on; a Stale one needs a rerun. */
  current: boolean;
  /** A Run on sample's Result newer than every full one, shown instead, marked as a sample. */
  sample: { run: WorkbookRun; result: WorkbookResult } | undefined;
};

export type WorkbookRunResults = {
  /** Newest first, as listed. */
  runs: readonly WorkbookRun[];
  /** Each Worksheet's Fingerprint in the edited Definition. */
  fingerprints: ReadonlyMap<string, string>;
  latestRun: WorkbookRun | undefined;
  sheet(sheetId: string | null): SheetRunResult;
};

/**
 * Each Worksheet's Result from the listed Runs, by the one rule every surface and
 * the server share (`shownResult`), against the edited Definition's Fingerprints.
 */
export function workbookRunResults(
  runs: readonly WorkbookRun[] | undefined,
  fingerprints: ReadonlyMap<string, string>,
): WorkbookRunResults {
  const listed = runs ?? [];
  const latestRun = listed[0];
  return {
    runs: listed,
    fingerprints,
    latestRun,
    sheet: (sheetId) => ({
      latestRun,
      ...shownResult(listed, fingerprints, sheetId),
      sample: sampleResult(listed, fingerprints, sheetId),
    }),
  };
}

type Filter = WorkbookFilter;
const sameFilter = (left: Filter, right: Filter) =>
  left.column === right.column &&
  left.operator === right.operator &&
  left.value === right.value;

/**
 * The rows each of `filters` removed in the Result's Run, matched by content to
 * the filters the Snapshot ran, so edits elsewhere keep their counts. A filter
 * the Run did not have, or a Result without Filter counts, gets `undefined`.
 */
export function filterRemovals(
  result: WorkbookResult | undefined,
  filters: readonly Filter[],
): (number | undefined)[] {
  const ran = result?.filterCounts?.filters ?? [];
  const used = new Set<number>();
  return filters.map((filter) => {
    const index = ran.findIndex(
      (item, position) =>
        !used.has(position) && sameFilter(item.filter, filter),
    );
    if (index < 0) return undefined;
    used.add(index);
    return ran[index]?.removedRowCount;
  });
}

export type RunResultsEditor = Pick<WorkbookEditor, 'definition'> & {
  runs: Pick<WorkbookEditor['runs'], 'data'>;
};

/** Fingerprints follow the edited Definition, unsaved edits included. */
export function useWorkbookRunResults(
  editor: RunResultsEditor,
): WorkbookRunResults {
  const { definition } = editor;
  const runs = editor.runs.data;
  const fingerprints = useMemo(
    () => sheetFingerprints(definition),
    [definition],
  );
  return useMemo(
    () => workbookRunResults(runs, fingerprints),
    [runs, fingerprints],
  );
}
